import { computed, onBeforeUnmount, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { getActuatorManualProgress } from '@/api/deviceManage'

// 手动操作只提交一次。轮询只读结果；卸载页面取消本地等待，不取消设备指令。
export function useActuatorManualOperation() {
  const busy = ref(false)
  const progress = ref(null)
  const progressText = computed(() => progress.value?.displayMessage || '')
  let disposed = false
  let timer = null
  let finishDelay = null
  let notice = null

  const delay = ms => new Promise(resolve => {
    finishDelay = resolve
    timer = setTimeout(() => {
      timer = null
      finishDelay = null
      resolve()
    }, ms)
  })

  onBeforeUnmount(() => {
    disposed = true
    clearTimeout(timer)
    finishDelay?.()
    notice?.close()
  })

  const dataOf = response => {
    if (!response || ![0, 1000].includes(Number(response.code)) || !response.data?.recordId) {
      throw new Error(response?.msg || '未取得操作记录，请刷新设备数据确认，不要重复下发')
    }
    return response.data
  }

  const run = async (submit, { onProgress, operation = '操作' } = {}) => {
    if (busy.value || disposed) return null
    busy.value = true
    notice?.close()
    const display = current => {
      const next = { ...current, displayMessage: current.message ?? '' }
      progress.value = next
      onProgress?.(next)
      return next
    }
    let submitted = false
    try {
      display({ status: 'SENDING', message: '正在下发指令', finished: false })
      let current = dataOf(await submit())
      submitted = true
      let queryErrors = 0
      while (!disposed) {
        const shown = display(current)
        if (current.finished) {
          const type = current.status === 'SUCCESS' ? 'success' : current.status === 'FAILED' ? 'error' : 'warning'
          notice = ElMessage({ message: shown.displayMessage, type, showClose: true, duration: 5000 })
          return current
        }
        // 以服务端时间为准，即使剩余时间为0也再查询一次，由后端判定超时。
        await delay(2000)
        if (disposed) return null
        try {
          current = dataOf(await getActuatorManualProgress(current.recordType, current.recordId))
          queryErrors = 0
        } catch (error) {
          if (++queryErrors >= 3) throw error
        }
      }
      return null
    } catch (error) {
      if (!disposed) {
        notice?.close()
        const message = submitted
          ? '暂时无法查询操作结果，请稍后刷新设备数据确认，不要重复下发指令'
          : (error?.message || (typeof error === 'string' ? error : '') || '指令提交结果未确认，请刷新设备数据后再操作')
        display({ ...(progress.value || {}), status: 'UNKNOWN', message, finished: false })
        notice = ElMessage.warning(message)
      }
      return null
    } finally {
      busy.value = false
    }
  }

  const clearProgress = () => {
    if (busy.value) return
    progress.value = null
    notice?.close()
  }

  return { run, busy, progress, progressText, clearProgress }
}
